import React, { useState, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import SelectGroup from '../../inputs/select/grouped/select-group';
import InputMoney from '../../inputs/money/input-money';

// Interface para compatibilidade com o SelectGroup
interface GroupedOption {
  label: string;
  value: any;
  name?: string;
  id?: any;
  group?: string;
}

interface Rubrica {
  id: string;
  name: string;
  description: string;
  value: string;
  label: string;
  group?: string;
}

interface RubricaFieldProps {
  index: number;
  availableRubricas: Rubrica[];
  onRemove: () => void;
  isLoading: boolean;
  isLoadingRubricas: boolean;
  canRemove: boolean;
  getUsedRubricaIds: () => string[];
  fieldId: string;
}

export default function RubricaField({
  index,
  availableRubricas,
  onRemove,
  isLoading,
  isLoadingRubricas,
  canRemove,
  getUsedRubricaIds,
  fieldId
}: RubricaFieldProps) {
  const { control, setValue, getValues } = useFormContext();
  
  const [selectedRubricaId, setSelectedRubricaId] = useState<string>(() => {
    const initialValue = getValues(`rubricas.${index}.rubricaId`);
    return initialValue || '';
  });
  
  const isUpdatingRef = useRef(false);

  const getAvailableRubricasForField = (): GroupedOption[] => {
    const usedIds = getUsedRubricaIds();
    
    // Filtrar rubricas disponíveis, mantendo a selecionada atual
    const filtered = availableRubricas.filter(rubrica => {
      return rubrica.id === selectedRubricaId || !usedIds.includes(rubrica.id);
    });

    
    // Verificar se há grupos válidos
    const hasValidGroups = filtered.some(r => r.group && r.group.trim() !== '');

    // Formattar os dados no formato exato esperado pelo SelectGroup
    const formattedData = filtered.map(rubrica => {
      const formatted = {
        label: rubrica.label,
        value: rubrica.value,
        name: rubrica.name,
        id: rubrica.id,
        group: rubrica.group || 'Sem grupo'
      };
      
      return formatted;
    });
    
    return formattedData;
  };

  const getSelectedRubricaObject = (): GroupedOption | null => {
    if (!selectedRubricaId) return null;
    
    // Buscar tanto por ID quanto por value, pois podem ser diferentes
    const rubrica = availableRubricas.find(r => 
      r.id === selectedRubricaId || r.value === selectedRubricaId
    );
    if (!rubrica) return null;
    
    // Retornar no formato exato esperado pelo SelectGroup
    return {
      label: rubrica.label,
      value: rubrica.value,
      name: rubrica.name,
      id: rubrica.id,
      group: rubrica.group || 'Outros'
    };
  };

  const handleRubricaChange = (newValue: string | null) => {
    isUpdatingRef.current = true;
    const valueToSet = newValue || '';
    
    setSelectedRubricaId(valueToSet);
    
    setValue(`rubricas.${index}.rubricaId`, valueToSet, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
    
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 100);
  };

  // Sincronizar com mudanças do formulário
  useEffect(() => {
    if (!isUpdatingRef.current) {
      const formValue = getValues(`rubricas.${index}.rubricaId`);
      if (formValue !== selectedRubricaId) {
        setSelectedRubricaId(formValue || '');
      }
    }
  }, [index, getValues]);

  return (
    <div className="rubrica-field" key={`rubrica-field-${fieldId}`}>
      <div className="rubrica-field__row">
        <div className="rubrica-field__select">
          <Controller
            name={`rubricas.${index}.rubricaId`}
            control={control}
            rules={{ required: 'Selecione uma rubrica' }}
            render={({ field, fieldState: { error } }) => {
              const selectedObject = getSelectedRubricaObject();
              const availableOptions = getAvailableRubricasForField();
                        
              return (
                <SelectGroup
                  key={`select-${fieldId}-${selectedRubricaId}-${availableOptions.length}`}
                  data={availableOptions}
                  onChange={handleRubricaChange}
                  value={selectedRubricaId} // Passar apenas a string do ID
                  placeholder="Selecione uma rubrica"
                  error={error?.message}
                  disabled={isLoading || isLoadingRubricas}
                  height={55}
                  width="100%"
                />
              );
            }}
          />
        </div>

        <div className="rubrica-field__value">
          <Controller
            name={`rubricas.${index}.valor`}
            control={control}
            rules={{
              required: 'Informe o valor',
              min: { value: 0.01, message: 'Valor deve ser maior que zero' },
              validate: (value) => {
                const numValue = Number(value);
                if (isNaN(numValue) || numValue <= 0) {
                  return 'Valor deve ser maior que zero';
                }
                return true;
              }
            }}
            render={({ field: { onChange, value, name }, fieldState: { error } }) => (
              <InputMoney
                key={`money-${fieldId}`}
                name={name}
                value={value}
                onChange={(e) => {
                  const numericValue = Number(e.target.value);
                  onChange(numericValue);
                }}
                placeholder="Valor"
                error={error?.message}
                disabled={isLoading || isLoadingRubricas}
                width="100%"
                height={55}
                prefix="R$ "
              />
            )}
          />
        </div>

        <div className="rubrica-field__actions">
          <button
            type="button"
            onClick={onRemove}
            className="rubrica-field__remove"
            disabled={!canRemove || isLoading}
            title="Remover rubrica"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
}